<template>
  <div
    v-if="isVisible"
    :class="toastClasses"
    class="relative flex items-start gap-3 p-4 rounded-lg border shadow-lg transition-all duration-300 ease-in-out"
    :style="toastStyles"
  >
    <!-- Icon -->
    <div :class="iconClasses" class="flex-shrink-0">
      <component :is="iconComponent" class="h-5 w-5" />
    </div>

    <!-- Content -->
    <div class="flex-1 min-w-0">
      <div v-if="title" :class="titleClasses" class="font-medium">
        {{ title }}
      </div>
      <div v-if="description" :class="descriptionClasses" class="text-sm mt-1">
        {{ description }}
      </div>
    </div>

    <!-- Close Button -->
    <button
      v-if="closable"
      @click="handleClose"
      :class="closeButtonClasses"
      class="flex-shrink-0 p-1 rounded-md hover:bg-black/10 transition-colors"
    >
      <X class="h-4 w-4" />
    </button>

    <!-- Progress Bar -->
    <div
      v-if="showProgress && duration > 0"
      class="absolute bottom-0 left-0 right-0 h-1 bg-black/10 rounded-b-lg overflow-hidden"
    >
      <div
        class="h-full bg-current opacity-30 transition-all ease-linear"
        :style="{ width: `${progressWidth}%` }"
      />
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-vue-next'

const toastVariants = {
  success: {
    container: 'bg-green-50 border-green-200 text-green-900',
    icon: 'text-green-500',
    title: 'text-green-900',
    description: 'text-green-700',
    closeButton: 'text-green-600 hover:text-green-800'
  },
  error: {
    container: 'bg-red-50 border-red-200 text-red-900',
    icon: 'text-red-500',
    title: 'text-red-900',
    description: 'text-red-700',
    closeButton: 'text-red-600 hover:text-red-800'
  },
  warning: {
    container: 'bg-yellow-50 border-yellow-200 text-yellow-900',
    icon: 'text-yellow-500',
    title: 'text-yellow-900',
    description: 'text-yellow-700',
    closeButton: 'text-yellow-600 hover:text-yellow-800'
  },
  info: {
    container: 'bg-blue-50 border-blue-200 text-blue-900',
    icon: 'text-blue-500',
    title: 'text-blue-900',
    description: 'text-blue-700',
    closeButton: 'text-blue-600 hover:text-blue-800'
  }
}

const iconMap = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info
}

export default {
  name: 'Toast',
  props: {
    id: {
      type: [String, Number],
      required: true
    },
    type: {
      type: String,
      default: 'info',
      validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
    },
    title: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    duration: {
      type: Number,
      default: 5000
    },
    closable: {
      type: Boolean,
      default: true
    },
    showProgress: {
      type: Boolean,
      default: true
    },
    position: {
      type: String,
      default: 'top-right',
      validator: (value) => ['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(value)
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const isVisible = ref(true)
    const progressWidth = ref(100)
    const startTime = ref(Date.now())

    const variant = computed(() => toastVariants[props.type] || toastVariants.info)
    
    const toastClasses = computed(() => [
      variant.value.container,
      'animate-in slide-in-from-right-full duration-300'
    ])

    const iconClasses = computed(() => variant.value.icon)
    const titleClasses = computed(() => variant.value.title)
    const descriptionClasses = computed(() => variant.value.description)
    const closeButtonClasses = computed(() => variant.value.closeButton)

    const iconComponent = computed(() => iconMap[props.type] || iconMap.info)

    const toastStyles = computed(() => {
      const positions = {
        'top-left': { top: '1rem', left: '1rem' },
        'top-right': { top: '1rem', right: '1rem' },
        'bottom-left': { bottom: '1rem', left: '1rem' },
        'bottom-right': { bottom: '1rem', right: '1rem' }
      }
      return positions[props.position] || positions['top-right']
    })

    let progressInterval = null

    const updateProgress = () => {
      if (props.duration > 0) {
        const elapsed = Date.now() - startTime.value
        const remaining = Math.max(0, props.duration - elapsed)
        progressWidth.value = (remaining / props.duration) * 100
      }
    }

    const handleClose = () => {
      isVisible.value = false
      setTimeout(() => {
        emit('close', props.id)
      }, 300) // Wait for animation to complete
    }

    onMounted(() => {
      if (props.duration > 0 && props.showProgress) {
        progressInterval = setInterval(updateProgress, 50)
      }
    })

    onUnmounted(() => {
      if (progressInterval) {
        clearInterval(progressInterval)
      }
    })

    return {
      isVisible,
      progressWidth,
      toastClasses,
      iconClasses,
      titleClasses,
      descriptionClasses,
      closeButtonClasses,
      iconComponent,
      toastStyles,
      handleClose
    }
  }
}
</script>
